import React from 'react';
import { useTranslation } from '../lib/i18n';

type PasswordStrengthProps = {
  password?: string;
};

export default function PasswordStrength({ password = '' }: PasswordStrengthProps) {
  const { t } = useTranslation();

  if (!password) {
    return null;
  }

  // Calculate score
  let score = 0;
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const isLongEnough = password.length >= 8;

  if (hasLower) score += 1;
  if (hasUpper) score += 1;
  if (hasNumber) score += 1;
  if (hasSpecial) score += 1;
  if (isLongEnough) score += 1;

  // Level classification
  let levelText = '';
  let colorClass = '';
  let barCount = 0;

  if (password.length < 6) {
    levelText = t('passwordTooShortHelp');
    colorClass = 'bg-rust';
    barCount = 1;
  } else if (score <= 2) {
    levelText = t('pwdWeak');
    colorClass = 'bg-rust';
    barCount = 2;
  } else if (score === 3 || score === 4) {
    levelText = t('pwdMedium');
    colorClass = 'bg-ochre';
    barCount = 3;
  } else {
    levelText = t('pwdStrong');
    colorClass = 'bg-moss';
    barCount = 4;
  }

  return (
    <div className="mt-2 space-y-1.5 animate-slide-in">
      <div className="flex justify-between items-center text-xs">
        <span className="text-wood font-medium">
          {password.length < 8 ? `${password.length}/8` : `${password.length}`} {t('password')}
        </span>
        <span className={`font-semibold ${password.length < 6 ? 'text-rust' : score <= 2 ? 'text-rust' : score <= 4 ? 'text-ochre dark:text-ochre' : 'text-moss-dark'}`}>
          {levelText}
        </span>
      </div>
      <div className="flex gap-1 h-1.5 w-full bg-wheat/20 dark:bg-wheat/10 rounded-full overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-full flex-1 transition-all duration-300 rounded-full ${
              i <= barCount ? colorClass : 'bg-transparent'
            }`}
          />
        ))}
      </div>
      {password.length >= 6 && (
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs text-wood/80  pt-1 border-t border-wheat/20">
          <div className="flex items-center gap-1.5">
            <span className={isLongEnough ? 'text-moss-dark font-bold' : 'text-wood/70'}>
              {isLongEnough ? '✓' : '○'}
            </span>
            <span>{t('pwdMinLengthTip')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={hasUpper ? 'text-moss-dark font-bold' : 'text-wood/70'}>
              {hasUpper ? '✓' : '○'}
            </span>
            <span>A-Z</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={hasNumber ? 'text-moss-dark font-bold' : 'text-wood/70'}>
              {hasNumber ? '✓' : '○'}
            </span>
            <span>0-9</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={hasSpecial ? 'text-moss-dark font-bold' : 'text-wood/70'}>
              {hasSpecial ? '✓' : '○'}
            </span>
            <span>!@#$%^&*</span>
          </div>
        </div>
      )}
    </div>
  );
}
